import { supabase } from '../lib/supabase';

export const realtimeDashboardService = {
  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS FOR DASHBOARD UPDATES
  // ============================================================================

  /**
   * Subscribe to compliance violations in real-time
   * Monitors INSERT and UPDATE events on compliance_violations table
   */
  subscribeToViolations(callback, departmentFilter = null) {
    const channel = supabase
      ?.channel('violations_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_violations'
        },
        (payload) => {
          const violation = {
            id: payload?.new?.id,
            violationType: payload?.new?.violation_type,
            severity: payload?.new?.severity,
            violationStatus: payload?.new?.violation_status,
            description: payload?.new?.description,
            department: payload?.new?.department,
            affectedCount: payload?.new?.affected_count,
            estimatedCost: payload?.new?.estimated_cost,
            identifiedAt: payload?.new?.identified_at,
            createdAt: payload?.new?.created_at
          };

          // Filter by department if specified
          if (!departmentFilter || departmentFilter === 'all' || violation?.department === departmentFilter) {
            callback(violation, payload?.eventType);
          }
        }
      )
      ?.subscribe();
    
    return channel;
  },

  /**
   * Subscribe to payroll errors in real-time
   * Monitors payroll_validation_results for failed validations
   */
  subscribeToPayrollErrors(callback, departmentFilter = null) {
    const channel = supabase
      ?.channel('payroll_errors_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payroll_validation_results',
          filter: 'validation_status=eq.failed'
        },
        async (payload) => {
          // Fetch employee details for department filtering
          const { data: employee } = await supabase
            ?.from('employees')
            ?.select('department, first_name, last_name')
            ?.eq('id', payload?.new?.employee_id)
            ?.single();

          const error = {
            id: payload?.new?.id,
            employeeId: payload?.new?.employee_id,
            employeeName: employee ? `${employee?.first_name} ${employee?.last_name}` : 'Unknown',
            department: employee?.department,
            validationMessage: payload?.new?.validation_message,
            expectedValue: payload?.new?.expected_value,
            actualValue: payload?.new?.actual_value,
            varianceAmount: payload?.new?.variance_amount,
            severity: payload?.new?.severity,
            validatedAt: payload?.new?.validated_at
          };

          // Filter by department if specified
          if (!departmentFilter || departmentFilter === 'all' || error?.department?.toLowerCase() === departmentFilter) {
            callback(error);
          }
        }
      )
      ?.subscribe();
    
    return channel;
  },

  /**
   * Subscribe to compliance deadlines in real-time
   * Monitors NASFUND and SWT filing deadlines
   */
  subscribeToComplianceDeadlines(callback) {
    const nasfundChannel = supabase
      ?.channel('nasfund_deadlines_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'nasfund_contributions'
        },
        (payload) => {
          const deadline = {
            id: payload?.new?.id,
            type: 'nasfund',
            title: 'NASFUND Contribution Deadline',
            period: payload?.new?.contribution_period,
            dueDate: payload?.new?.due_date,
            filingStatus: payload?.new?.filing_status,
            totalAmount: payload?.new?.total_employee_contribution + payload?.new?.total_employer_contribution,
            createdAt: payload?.new?.created_at
          };
          callback(deadline, payload?.eventType);
        }
      )
      ?.subscribe();

    const swtChannel = supabase
      ?.channel('swt_deadlines_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'swt_filings'
        },
        (payload) => {
          const deadline = {
            id: payload?.new?.id,
            type: 'swt',
            title: 'SWT Filing Deadline',
            period: payload?.new?.filing_period,
            dueDate: payload?.new?.due_date,
            filingStatus: payload?.new?.filing_status,
            totalAmount: payload?.new?.total_swt,
            createdAt: payload?.new?.created_at
          };
          callback(deadline, payload?.eventType);
        }
      )
      ?.subscribe();
    
    return { nasfundChannel, swtChannel };
  },

  /**
   * Subscribe to all dashboard updates (violations, errors, deadlines)
   */
  subscribeToAllUpdates(callbacks, departmentFilter = null) {
    const channels = {
      violations: this.subscribeToViolations(callbacks?.onViolation, departmentFilter),
      payrollErrors: this.subscribeToPayrollErrors(callbacks?.onPayrollError, departmentFilter),
      deadlines: this.subscribeToComplianceDeadlines(callbacks?.onDeadline)
    };

    return channels;
  },

  /**
   * Unsubscribe from all channels
   */
  unsubscribeAll(channels) {
    if (channels?.violations) {
      supabase?.removeChannel(channels?.violations);
    }
    if (channels?.payrollErrors) {
      supabase?.removeChannel(channels?.payrollErrors);
    }
    if (channels?.deadlines?.nasfundChannel) {
      supabase?.removeChannel(channels?.deadlines?.nasfundChannel);
    }
    if (channels?.deadlines?.swtChannel) {
      supabase?.removeChannel(channels?.deadlines?.swtChannel);
    }
  }
};

export default realtimeDashboardService;
